import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { useInput, useNotify, type Identifier } from "ra-core";
import { useWatch } from "react-hook-form";
import { FormField, FormLabel, SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { invocarFuncao } from "@/lib/edgeFunction";
import { cn } from "@/lib/utils";

import { supabase } from "../providers/supabase/supabase";

/**
 * Responsável por um lead ou contato (migrations 210 e 211).
 *
 * Trocar o responsável NÃO é gravar `user_id`: a RLS não deixa ninguém tirar
 * uma linha do próprio escopo, e o contato leva os leads dele junto. Quem troca
 * é o banco (`crm_enviar_lead` / `crm_enviar_contato`, pela edge function
 * `enviar-lead`, que também manda o e-mail). Por isso o campo do formulário é
 * VIRTUAL (`responsavel_id`, sem coluna): o formulário grava o que é dele,
 * separa a escolha (`separar`) e, com o cadastro salvo, pede a troca
 * (`aplicar`).
 */

export const CAMPO_RESPONSAVEL = "responsavel_id";

export type Destinatario = { id: number; name: string; eu: boolean };
export type AlvoDaTroca = { dealId: Identifier } | { contactId: Identifier };
type RespostaTroca = { para: { name: string }; leads: number; emailEnviado: boolean; emailErro: string | null };

type Dono = Identifier | null | undefined;

const mudou = (escolhido: unknown, donoAtual: Dono) =>
  escolhido != null && escolhido !== "" && Number(escolhido) !== Number(donoAtual ?? Number.NaN);

/** Quem pode ser responsável — já filtrado pelas regras do banco. */
export const useDestinatarios = (ativo = true) =>
  useQuery({
    queryKey: ["crm_destinatarios_lead"],
    enabled: ativo,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("crm_destinatarios_lead" as never);
      if (error) throw error;
      return (data ?? []) as Destinatario[];
    },
  });

/**
 * Quantos leads esses contatos têm, arquivados inclusive — todos mudam de dono
 * junto com o contato. Contado com a RLS de quem pergunta, que é o mesmo
 * recorte do que o banco vai mover.
 */
export const useLeadsDosContatos = (contatoIds: Identifier[] | undefined, ativo = true) => {
  const ids = (contatoIds ?? []).map(Number).filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b);
  return useQuery({
    queryKey: ["crm_leads_dos_contatos", ids],
    enabled: ativo && ids.length > 0,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("deals")
        .select("id", { count: "exact", head: true })
        .overlaps("contact_ids", ids);
      if (error) throw error;
      return count ?? 0;
    },
  });
};

/**
 * A frase da confirmação. `leadsDoCliente` conta TODOS os leads do cliente;
 * pelo lead, o próprio lead sai da conta dos "outros" quando já existe.
 */
export const textoDaTroca = ({
  tipo,
  nome,
  eu,
  leadsDoCliente,
  leadJaExiste,
}: {
  tipo: "lead" | "contato";
  nome: string;
  eu: boolean;
  leadsDoCliente: number;
  leadJaExiste: boolean;
}) => {
  const quem = eu ? "você" : nome;
  const aviso = eu ? "" : ` ${nome} recebe aviso por e-mail.`;
  if (tipo === "lead") {
    const outros = Math.max(leadsDoCliente - (leadJaExiste ? 1 : 0), 0);
    const junto =
      outros === 0 ? "" : outros === 1 ? " O outro lead deste cliente também é transferido." : ` Os outros ${outros} leads deste cliente também são transferidos.`;
    return `O lead e o cliente passam para ${quem}.${junto}${aviso}`;
  }
  const leads =
    leadsDoCliente === 0
      ? "O contato passa"
      : leadsDoCliente === 1
        ? "O contato e o lead dele passam"
        : `O contato e todos os ${leadsDoCliente} leads dele passam`;
  return `${leads} para ${quem}.${aviso}`;
};

/** Pede a troca ao banco e avisa na tela. Devolve se trocou. */
export const trocarResponsavel = async (
  alvo: AlvoDaTroca,
  paraUserId: number,
  notify: ReturnType<typeof useNotify>,
): Promise<boolean> => {
  const body =
    "dealId" in alvo
      ? { dealId: Number(alvo.dealId), paraUserId }
      : { contactId: Number(alvo.contactId), paraUserId };
  const { dados, erro } = await invocarFuncao<RespostaTroca>("enviar-lead", {
    body,
    quando: "não foi possível trocar o responsável",
  });
  if (erro || !dados) {
    notify(`Não foi possível trocar o responsável: ${erro ?? "erro desconhecido"}`, { type: "error" });
    return false;
  }
  const leads = dados.leads > 0 && !("dealId" in alvo) ? ` com ${dados.leads} lead(s)` : "";
  const outros = "dealId" in alvo && dados.leads > 1 ? ` (com mais ${dados.leads - 1} lead(s) do mesmo cliente)` : "";
  const feito = `${"dealId" in alvo ? "Lead enviado" : "Contato enviado"} para ${dados.para.name}${leads}${outros}`;
  if (dados.emailEnviado) {
    notify(`${feito}. Aviso enviado por e-mail.`, { type: "success" });
  } else {
    notify(`${feito}. Não foi possível enviar o aviso por e-mail (${dados.emailErro ?? "erro desconhecido"}).`, { type: "warning" });
  }
  return true;
};

/**
 * O par separar/aplicar dos formulários. `separar` vai no `transform` (tira o
 * campo virtual, que não é coluna, e guarda a escolha); `aplicar` vai no
 * sucesso da gravação, com o id do que foi salvo.
 */
export const useTrocaAoSalvar = () => {
  const notify = useNotify();
  const pendente = useRef<number | null>(null);

  const separar = <T extends object>(dados: T, donoAtual: Dono): T => {
    const { [CAMPO_RESPONSAVEL]: escolhido, ...resto } = dados as Record<string, unknown>;
    pendente.current = mudou(escolhido, donoAtual) ? Number(escolhido) : null;
    return resto as T;
  };

  const aplicar = async (alvo: AlvoDaTroca): Promise<boolean> => {
    const para = pendente.current;
    pendente.current = null;
    if (para == null) return false;
    return trocarResponsavel(alvo, para, notify);
  };

  return { separar, aplicar };
};

/**
 * Caixa de escolher pessoa, com busca pelo nome. Não é o `AutocompleteInput`
 * do kit: com opções locais ele filtra pelo `value` do item, que lá é o id —
 * digitar "Bruno" não acharia ninguém. Serve ao formulário (`ResponsavelInput`)
 * e ao "Enviar para…" do card, que não tem formulário em volta.
 */
export const SeletorDePessoa = ({
  id,
  valor,
  aoMudar,
  pessoas = [],
  carregando = false,
  desativado = false,
  vazio = "Selecione o usuário",
}: {
  id?: string;
  valor: number | null;
  aoMudar: (id: number) => void;
  pessoas?: { id: number; name: string }[];
  carregando?: boolean;
  desativado?: boolean;
  /** O que aparece quando ninguém está escolhido. */
  vazio?: string;
}) => {
  const [aberto, setAberto] = useState(false);
  const atual = pessoas.find((p) => p.id === valor);
  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aberto}
          disabled={desativado}
          className="w-full justify-between font-normal"
        >
          {atual ? atual.name : <span className="text-muted-foreground">{carregando ? "Carregando…" : vazio}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar pelo nome…" />
          <CommandList>
            <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
            <CommandGroup className="max-h-[240px] overflow-y-auto">
              {pessoas.map((p) => (
                <CommandItem
                  key={p.id}
                  // O cmdk busca pelo `value`: o nome, e o id só para não
                  // repetir quando duas pessoas têm o mesmo nome.
                  value={`${p.name} #${p.id}`}
                  onSelect={() => {
                    aoMudar(p.id);
                    setAberto(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", p.id === valor ? "opacity-100" : "opacity-0")} />
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** O campo. `donoAtual`: quem é responsável hoje (na criação, quem cria). */
export const ResponsavelInput = ({ donoAtual: dono }: { donoAtual: Dono }) => {
  const donoAtual = dono == null || dono === "" ? null : Number(dono);
  const { data: destinatarios = [], isPending } = useDestinatarios();
  const { id, field } = useInput({ source: CAMPO_RESPONSAVEL, defaultValue: donoAtual ?? undefined });
  const pessoas = destinatarios.map((d) => ({ id: d.id, name: d.eu ? `${d.name} (eu)` : d.name }));
  // Dono fora da lista (desativado, por exemplo): continua aparecendo como o
  // valor atual, em vez de o campo abrir vazio e parecer "sem responsável".
  if (donoAtual != null && !pessoas.some((p) => p.id === donoAtual)) {
    pessoas.unshift({ id: donoAtual, name: "Responsável atual" });
  }
  return (
    <FormField id={id} name={field.name}>
      <FormLabel>Responsável</FormLabel>
      <SeletorDePessoa
        id={id}
        valor={field.value == null || field.value === "" ? null : Number(field.value)}
        aoMudar={(v) => field.onChange(v)}
        pessoas={pessoas}
        carregando={isPending}
        vazio="Sem responsável"
      />
    </FormField>
  );
};

/**
 * Salvar que confirma antes quando o responsável mudou — e diz quantos leads
 * vão junto. Sem troca, é o Salvar de sempre.
 */
export const SalvarComResponsavel = ({
  label,
  tipo,
  donoAtual,
  contatoIds = [],
  leadJaExiste = false,
}: {
  label?: string;
  tipo: "lead" | "contato";
  donoAtual: Dono;
  /** Contatos cujos leads vão junto — é daqui que sai a contagem da confirmação. */
  contatoIds?: Identifier[];
  leadJaExiste?: boolean;
}) => {
  const escolhido = useWatch({ name: CAMPO_RESPONSAVEL });
  const para = mudou(escolhido, donoAtual) ? Number(escolhido) : null;
  const { data: destinatarios } = useDestinatarios(para != null);
  const { data: leadsDoCliente = 0 } = useLeadsDosContatos(contatoIds, para != null);
  const [perguntando, setPerguntando] = useState(false);
  const confirmado = useRef(false);
  const caixa = useRef<HTMLSpanElement>(null);

  const destino = destinatarios?.find((d) => d.id === para);

  return (
    <>
      <span ref={caixa} className="contents">
        <SaveButton
          label={label}
          onClick={(e) => {
            if (para != null && !confirmado.current) {
              e.preventDefault();
              setPerguntando(true);
              return;
            }
            confirmado.current = false;
          }}
        />
      </span>
      <Dialog open={perguntando} onOpenChange={setPerguntando}>
        <DialogContent className="z-[70] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Trocar o responsável?</DialogTitle>
            <DialogDescription>
              {textoDaTroca({
                tipo,
                nome: destino?.name ?? "a pessoa escolhida",
                eu: destino?.eu ?? false,
                leadsDoCliente,
                leadJaExiste,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPerguntando(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => {
                setPerguntando(false);
                confirmado.current = true;
                caixa.current?.querySelector("button")?.click();
              }}
            >
              Confirmar e salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
